import axios, { ResponseType } from 'axios';
import { call, put, takeEvery, takeLatest } from 'redux-saga/effects';

import { getTokenRedirect } from 'src/auth/auth';
import { REQUEST_API, REQUEST_API_HOLDING_DATA } from 'src/constants/index';
import { resetCustomLoginDataRequest } from 'src/redux/auth/actions';
import store from 'src/redux/store';
import { getActionAPISource, replaceParamsInUrl, sendGAEvent } from 'src/utils';
import {
  receiveAPIResponse,
  REQUEST_API_LATEST,
  requestAPIFailed,
} from './actions';

export async function apiCallAsync(
  url: string,
  params: any = {},
  method = 'get',
  responseType: ResponseType | null = null,
  additionalHeaders: Record<string, string> = {},
  useAccessToken = false,
): Promise<any> {
  return getTokenRedirect()
    .then(authResponse => {
      if (!authResponse)
        throw new Error('Auth error. Token redirect response is empty');
      const token =
        !useAccessToken && 'idToken' in authResponse
          ? authResponse.idToken
          : authResponse.accessToken;
      const authToken = `Bearer ${token}`;
      sendGAEvent('api_service_called', { wfp_url: url });
      return fetchData(
        url,
        params,
        method,
        authToken,
        responseType,
        additionalHeaders,
      );
    })
    .catch(error => {
      if (
        error?.response?.data?.code === 'token_not_valid' &&
        error?.response?.data?.detail ===
          'Given token not valid for any token type'
      ) {
        store.dispatch(resetCustomLoginDataRequest());
        return;
      } else {
        throw error;
      }
    });
}

export function fetchData(
  url: string,
  params: any = {},
  method = 'get',
  authToken?: string,
  responseType?: ResponseType | null,
  additionalHeaders: Record<string, string> = {},
) {
  if (!authToken) {
    console.warn(
      `Please update your fetchData call with using authToken! ${url}`,
    );
    return null;
  }

  const headers = {
    Authorization: authToken,
    ...additionalHeaders,
  };

  const customLoginData = store.getState()?.auth;
  if (customLoginData?.accessToken) {
    headers['X-Custom-Auth'] = 'testuser';
  }

  const kwargs: any = {
    url,
    method,
    headers,
  };
  if (responseType) kwargs['responseType'] = responseType;

  if (method === 'get') {
    const serializedParams: Record<string, any> = { ...params };
    for (const key in serializedParams) {
      if (
        typeof serializedParams[key] === 'object' &&
        serializedParams[key] !== null
      ) {
        serializedParams[key] = JSON.stringify(serializedParams[key]);
      }
    }
    kwargs['params'] = serializedParams;
    kwargs['paramsSerializer'] = (params: Record<string, string>) =>
      new URLSearchParams(params).toString();
  } else if (['post', 'put', 'patch', 'delete'].includes(method)) {
    kwargs.headers['X-CSRFToken'] = window.djconfig.csrfToken;
    kwargs.data = params ? params : {};
  }

  return axios(kwargs);
}

interface ApiCallAction {
  type?: string;
  dataKey?: string;
  params?: any;
}

function getStorageKey(action: ApiCallAction) {
  if (
    action.hasOwnProperty('params') &&
    action.params.hasOwnProperty('storage_key')
  ) {
    return action.params.storage_key;
  }
  return action.dataKey;
}

const isSuccessResponse = (response: any) =>
  response?.status >= 200 && response?.status < 300;

function* putActions(actions?: any[]) {
  if (!actions) return;
  for (let i = 0, len = actions.length; i < len; i++) {
    yield put(actions[i]);
  }
}

export function* ApiCallSaga(action: ApiCallAction) {
  const storage_key = getStorageKey(action);

  try {
    let url = getActionAPISource(action.dataKey);
    url = replaceParamsInUrl(url, action.params);

    const response = yield call(
      apiCallAsync,
      url,
      action.params.data,
      action.params.method,
    );

    if (!isSuccessResponse(response)) {
      throw new Error('API call has failed');
    }

    yield put(receiveAPIResponse(storage_key, response.data, action.params));
    yield* putActions(action.params.success_actions);
  } catch (e) {
    yield put(
      requestAPIFailed(storage_key, {
        message: e.message,
        data: e.response ? e.response.data : null,
      }),
    );
    console.error(e);
    yield* putActions(action.params.failure_actions);
  }
}

export function* watchApiCallsSaga() {
  yield takeEvery(REQUEST_API, ApiCallSaga);
  yield takeEvery(REQUEST_API_HOLDING_DATA, ApiCallSaga);
  yield takeLatest(REQUEST_API_LATEST, ApiCallSaga);
}
